import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  getAddressesByUserId,
  updateUserAddress,
  createAddress,
  deleteAddressById,
}
from "../../redux/reducer/userAddressReducer";
import { MapPinIcon ,XMarkIcon } from "@heroicons/react/24/outline";
import CustomTitleAccount from "./customTitleAccount";
import CustomInfoInput from "./customInfoInput";
import { useTranslation } from "react-i18next";
import CustomDropdownCountryAddress from "../sharedComponents/CustomDropdownCountryAddress";
import { getMenuItemsLocationTo } from "../../constent/countries";
import { useLanguageNavigate } from "../../hooks/useLanguageNavigation";

const AccountAddressSection = ({ addresses }) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [formData, setFormData] = useState({});
  const [originalData, setOriginalData] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const navigateWithLang = useLanguageNavigate();

  const dispatch = useDispatch();
  const { t } = useTranslation();

  useEffect(() => {
    if (addresses?.addresses?.length) {
      const address = addresses.addresses[selectedTab];
      if (address) {
        setFormData({
          firstName: address.firstName || "",
          lastName: address.lastName || "",
          fullAddress: address.fullAddress || "",
          city: address.city || "",
          country: address.country || "",
          state: address.state || "",
          postalCode: address.postalCode || "",
          _id: address._id || null,
        });
        setOriginalData({
          firstName: address.firstName || "",
          lastName: address.lastName || "",
          fullAddress: address.fullAddress || "",
          city: address.city || "",
          country: address.country || "",
          state: address.state || "",
          postalCode: address.postalCode || "",
          _id: address._id || null,
        });
        setIsEditing(false);
      }
    }
  }, [addresses, selectedTab]);

  const handleSave = () => {
    const updatedData = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      fullAddress: formData.fullAddress,
      city: formData.city,
      country: formData.country,
      state: formData.state,
      postalCode: formData.postalCode,
    };

    if (!formData._id) {
      dispatch(createAddress(updatedData))
      .unwrap()
        .then(() => {
          navigateWithLang("/my-account");
          dispatch(getAddressesByUserId());
          setIsEditing(false);
        })
        .catch((error) => console.error("Error creating address:", error));
      return;
    }

    if (JSON.stringify(updatedData) === JSON.stringify(originalData)) {
      alert(t("changesDetected"));
      return;
    }

    dispatch(updateUserAddress({ addressId: formData._id, updatedData }))
      .unwrap()
      .then(() => {
        navigateWithLang("/my-account");

        dispatch(getAddressesByUserId());
        setIsEditing(false);
      })
      .catch((error) => console.error("Error updating address:", error));
  };


  const handleInputChange = (key) => (e) => {
    const newFormData = {
      ...formData,
      [key]: e.target.value,
    };
    setFormData(newFormData);
    // We compare the original and new form data.
    const isChanged = JSON.stringify({
      firstName: newFormData.firstName,
      lastName: newFormData.lastName,
      fullAddress: newFormData.fullAddress,
      city: newFormData.city,
      country: newFormData.country,
      state: newFormData.state,
      postalCode: newFormData.postalCode,
    }) !== JSON.stringify({
      firstName: originalData.firstName,
      lastName: originalData.lastName,
      fullAddress: originalData.fullAddress,
      city: originalData.city,
      country: originalData.country,
      state: originalData.state,
      postalCode: originalData.postalCode,
    });
  
    setIsEditing(isChanged);
  };
  



  const handleTabClick = (index) => {
    setSelectedTab(index);
    setIsEditing(false); 
  };
  
  const handleDeleteAddress = (addressId) => {
    if (window.confirm(t("AreYouSureToDeleteAddress"))) {
      dispatch(deleteAddressById(addressId))
      .unwrap()
      .then(() => {
        dispatch(getAddressesByUserId());
          navigateWithLang("/my-account");

        })
        .catch((error) => {
          console.error("Failed to delete address:", error);
        });
    }
  };
  

  const menuItemsLocationTo = getMenuItemsLocationTo(t);
    // console.log(formData)

  return (
    <div className="flex flex-col gap-5">
      <CustomTitleAccount
        title={t("CurrentAddress")}
        icon={<MapPinIcon className="w-8 h-8 text-black" />}
        isEditing={isEditing}
        onClick={isEditing ? handleSave : null}
      />
  

      {(!addresses?.addresses || addresses?.addresses.length === 0) ? (
        <div className="text-gray-500"></div>
      ) : (
        <>
          {/* Tabs */}
          <div className="flex gap-2 mb-2 md:mb-4">
          {addresses.addresses.map((address, index) => (
          <div key={address._id}   className={`flex items-center justify-between md:px-4 px-2 py-2 text-xs text-black duration-300 rounded lg:text-base
            ${selectedTab === index ? "bg-custom-yellow" : "bg-black text-white hover:bg-gray-600"}
          `}>
            <button
              onClick={() => handleTabClick(index)}
              className={` ${
                selectedTab === index
                  ? "md:me-4 me-2 "
                  : "md:me-4 me-2 "
              }`}
            >
              {t("AddressNo")} {index + 1}
            </button>

            {/* Button Delete */}
            <button
              onClick={() => handleDeleteAddress(address._id)}
              className="text-red-500 hover:text-red-700"
            >
              <XMarkIcon class={`h-4 w-4 ${selectedTab === index ? "text-black":"text-white"}`} />
            </button>
          </div>
        ))}

          </div>
  
          {/* Address Form */}
          {formData && (
            <>
              <div className="flex gap-3">
                <CustomInfoInput
                  id="firstName"
                  placeholder={t("FirstName")}
                  label={t("FirstName")}
                  type="text"
                  value={formData.firstName}
                  onChange={handleInputChange("firstName")}
                  autoComplete="off"
                  icon={<i className="fas fa-user"></i>}
                />
                <CustomInfoInput
                  id="lastName"
                  placeholder={t("LastName")}
                  label={t("LastName")}
                  type="text"
                  value={formData.lastName}
                  onChange={handleInputChange("lastName")}
                  autoComplete="off"
                  icon={<i className="fas fa-user"></i>}
                />
              </div>
  
  
              <div className="flex gap-3">
                <CustomDropdownCountryAddress
                  title={t("Country")}
                  menuItems={menuItemsLocationTo}
                  textColor="custom-gray"
                  onSelect={(item) => {
                    setFormData((prev) => ({
                      ...prev,
                      country: item.id, 
                    }));

                    // We update the isEditing state based on the comparison with the originalData
                    const newFormData = {
                      ...formData,
                      country: item.id,
                    };
                    const isChanged = JSON.stringify({
                      firstName: newFormData.firstName,
                      lastName: newFormData.lastName,
                      fullAddress: newFormData.fullAddress,
                      city: newFormData.city,
                      country: newFormData.country,
                      state: newFormData.state,
                      postalCode: newFormData.postalCode,
                    }) !== JSON.stringify({
                      firstName: originalData.firstName,
                      lastName: originalData.lastName,
                      fullAddress: originalData.fullAddress,
                      city: originalData.city,
                      country: originalData.country,
                      state: originalData.state,
                      postalCode: originalData.postalCode,
                    });
                    setIsEditing(isChanged);
                  }}
                  selected={formData.country}
                />

                <CustomInfoInput
                  id="city"
                  label={t("City")}
                  type="text"
                  placeholder={t("City")}
                  value={formData.city}
                  onChange={handleInputChange("city")}
                  autoComplete="off"
                  icon={<i className="fas fa-city"></i>}
                />
              </div>
  
              <div className="flex gap-3">
                <CustomInfoInput
                  id="postalCode"
                  label={t("PostalCode")}
                  type="text"
                  placeholder={t("PostalCode")}
                  value={formData.postalCode}
                  onChange={handleInputChange("postalCode")}
                  autoComplete="off"
                  icon={<i className="fas fa-mail-bulk"></i>}
                />
                <CustomInfoInput
                  id="state"
                  label={t("State/Province")}
                  type="text"
                  placeholder={t("State")}
                  value={formData.state}
                  onChange={handleInputChange("state")}
                  autoComplete="off"
                  icon={<i className="fas fa-road"></i>}
                />
              </div>

              
              <div className="flex">
                <CustomInfoInput
                  id="fullAddress"
                  label={t("FullAddress")}
                  type="text"
                  placeholder={t("FullAddress")}
                  value={formData.fullAddress}
                  onChange={handleInputChange("fullAddress")}
                  autoComplete="off"
                  icon={<i className="fas fa-map-marked-alt"></i>}
                />
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
  
};

export default AccountAddressSection;
